import { useCallback, useState } from 'react';
import type { DirectoryCategoryRef, DirectoryCategoryDraft } from '@domain/entities';
import { useContainer } from '../providers/container-context';
import { useToast } from '../providers/toast-context';
import { useAuth } from '../providers/auth-context';
import { useAsync } from '../hooks/use-async';
import { useRealtimeVersion } from '../providers/realtime-context';
import { useIsMobile } from '../hooks/use-is-mobile';
import { ConfirmModal, ErrorState, Modal, RefreshButton, StatusBadge, TableSkeleton, TextField } from '../components/ui';

const NEW_DRAFT: DirectoryCategoryDraft = { slug: '', label: '', sortOrder: 0 };

export function DirectoryCategoriesPage(): JSX.Element {
  const container = useContainer();
  const toast = useToast();
  const { can } = useAuth();
  const vDirectoryCategories = useRealtimeVersion('directory-categories');

  const isMobile = useIsMobile();
  const categories = useAsync(() => container.directoryCategories.list.execute(), [container, vDirectoryCategories]);
  const [editorId, setEditorId] = useState<string | null | undefined>(undefined);
  const [draft, setDraft] = useState<DirectoryCategoryDraft>(NEW_DRAFT);
  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState<{ title: string; body: string; confirmLabel?: string; danger?: boolean; fn: () => void } | null>(null);
  const [actionBusy, setActionBusy] = useState(false);

  const openEditor = useCallback((cat: DirectoryCategoryRef | null) => {
    if (cat === null) { setEditorId(null); setDraft(NEW_DRAFT); return; }
    setEditorId(cat.id);
    setDraft({ slug: cat.slug, label: cat.label, sortOrder: cat.sortOrder });
  }, []);

  const save = useCallback(async () => {
    if (editorId === undefined) return;
    setSaving(true);
    try {
      await container.directoryCategories.save.execute({ id: editorId, draft });
      toast.notify(editorId === null ? 'Catégorie ajoutée' : 'Catégorie mise à jour');
      setEditorId(undefined);
      categories.reload();
    } catch (error) {
      toast.notifyError(error);
    } finally {
      setSaving(false);
    }
  }, [container, editorId, draft, toast, categories]);

  const toggle = useCallback(async (cat: DirectoryCategoryRef) => {
    try {
      await container.directoryCategories.toggle.execute({ id: cat.id, currentIsActive: cat.isActive });
      categories.reload();
    } catch (error) { toast.notifyError(error); }
  }, [container, toast, categories]);

  const remove = useCallback(async (id: string) => {
    try {
      await container.directoryCategories.remove.execute(id);
      toast.notify('Catégorie supprimée');
      categories.reload();
    } catch (error) { toast.notifyError(error); }
  }, [container, toast, categories]);

  const rows = categories.data ?? [];

  return (
    <>
      <div className="bbm-toolbar">
        <div className="bbm-toolbar__start">
          <div className="bbm-toolbar__hint">
            Catégories affichées dans le répertoire des bonnes adresses
          </div>
        </div>
        <div className="bbm-toolbar__end">
          <button
            type="button"
            className="bbm-btn bbm-btn--primary"
            disabled={!can('addresses', 'create')}
            title={can('addresses', 'create') ? '' : 'Réservé au super-admin'}
            onClick={() => openEditor(null)}
          >
            + Ajouter une catégorie
          </button>
          <RefreshButton onRefresh={categories.reload} loading={categories.loading} />
        </div>
      </div>

      {categories.loading ? <TableSkeleton rows={6} /> : null}
      {categories.error ? <ErrorState status={categories.error.status} message={categories.error.message} onRetry={categories.reload} /> : null}

      {rows.length > 0 ? (
        isMobile ? (
          <div className="bbm-card-stack">
            {rows.map((cat) => (
              <div key={cat.id} className="bbm-mobile-card">
                <div className="bbm-mobile-card__head">
                  <div>
                    <div className="bbm-mobile-card__title">{cat.label}</div>
                    <div className="bbm-mobile-card__sub" style={{ fontFamily: 'monospace' }}>{cat.slug}</div>
                  </div>
                  <StatusBadge status={cat.isActive ? 'approved' : 'inactive'} label={cat.isActive ? 'Active' : 'Inactive'} />
                </div>
                <div className="bbm-mobile-card__fields">
                  <div className="bbm-mobile-card__field">
                    <span className="bbm-mobile-card__label">Ordre</span>
                    <span>{cat.sortOrder}</span>
                  </div>
                </div>
                <div className="bbm-mobile-card__actions">
                  <button type="button" className="bbm-btn" disabled={!can('addresses', 'edit')} onClick={() => setPending({
                    title: 'Modifier la visibilité ?',
                    body: 'La visibilité de cette catégorie sera modifiée.',
                    fn: () => void toggle(cat),
                  })}>{cat.isActive ? 'Désactiver' : 'Activer'}</button>
                  <button type="button" className="bbm-btn" disabled={!can('addresses', 'edit')} onClick={() => openEditor(cat)}>Modifier</button>
                  <button type="button" className="bbm-btn bbm-btn--danger" disabled={!can('addresses', 'delete')} onClick={() => setPending({
                    title: 'Supprimer cette catégorie ?',
                    body: 'Cette action est irréversible.',
                    confirmLabel: 'Supprimer',
                    danger: true,
                    fn: () => void remove(cat.id),
                  })}>Supprimer</button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bbm-panel">
            <div className="bbm-row bbm-row--head">
              <div style={{ width: 60 }}>Ordre</div>
              <div style={{ width: 200 }}>Libellé</div>
              <div style={{ width: 200 }}>Slug</div>
              <div style={{ width: 100 }}>Statut</div>
              <div style={{ marginLeft: 'auto' }}>Actions</div>
            </div>
            {rows.map((cat) => (
              <div key={cat.id} className="bbm-row">
                <div className="bbm-cell" style={{ width: 60 }}>{cat.sortOrder}</div>
                <div className="bbm-cell-strong" style={{ width: 200 }}>{cat.label}</div>
                <div className="bbm-cell" style={{ width: 200, fontFamily: 'monospace', fontSize: 12 }}>{cat.slug}</div>
                <div style={{ width: 100 }}>
                  <StatusBadge status={cat.isActive ? 'approved' : 'inactive'} label={cat.isActive ? 'Active' : 'Inactive'} />
                </div>
                <div className="bbm-row__actions">
                  <button type="button" className="bbm-btn" disabled={!can('addresses', 'edit')} onClick={() => setPending({
                    title: 'Modifier la visibilité ?',
                    body: 'La visibilité de cette catégorie sera modifiée.',
                    fn: () => void toggle(cat),
                  })}>
                    {cat.isActive ? 'Désactiver' : 'Activer'}
                  </button>
                  <button type="button" className="bbm-btn" disabled={!can('addresses', 'edit')} onClick={() => openEditor(cat)}>Modifier</button>
                  <button type="button" className="bbm-btn bbm-btn--danger" disabled={!can('addresses', 'delete')} onClick={() => setPending({
                    title: 'Supprimer cette catégorie ?',
                    body: 'Cette action est irréversible.',
                    confirmLabel: 'Supprimer',
                    danger: true,
                    fn: () => void remove(cat.id),
                  })}>
                    Supprimer
                  </button>
                </div>
              </div>
            ))}
          </div>
        )
      ) : null}

      {editorId !== undefined ? (
        <Modal
          title={editorId === null ? 'Nouvelle catégorie' : 'Modifier la catégorie'}
          width={480}
          onClose={() => setEditorId(undefined)}
          onSubmit={() => void save()}
          busy={saving}
        >
          <TextField label="Libellé" value={draft.label} onChange={(v) => setDraft({ ...draft, label: v })} />
          <TextField label="Slug (lettres minuscules, tirets)" value={draft.slug} onChange={(v) => setDraft({ ...draft, slug: v })} />
          <TextField label="Ordre d'affichage" value={String(draft.sortOrder)} onChange={(v) => setDraft({ ...draft, sortOrder: Number(v) || 0 })} />
        </Modal>
      ) : null}
      {pending !== null ? (
        <ConfirmModal
          title={pending.title}
          body={pending.body}
          confirmLabel={pending.confirmLabel}
          danger={pending.danger}
          busy={actionBusy}
          onConfirm={() => {
            setActionBusy(true);
            Promise.resolve(pending.fn()).finally(() => {
              setActionBusy(false);
              setPending(null);
            });
          }}
          onCancel={() => setPending(null)}
        />
      ) : null}
    </>
  );
}
